import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

interface HeaderProps {
  openPopup?: (config?: {
    title?: string;
    subtitle?: string;
    formType?: string;
    interestedProgram?: string;
  }) => void;
}

const Header: React.FC<HeaderProps> = ({ openPopup }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
      {/* Top blue banner */}
      <div className="bg-blue-600 text-white text-center py-2 text-sm">
        <span className="flex items-center justify-center gap-2">
          <span className="hidden sm:inline">🚀 Transform your career with our industry-leading tech courses - </span>
          <button 
            onClick={() => openPopup?.({
              title: "Book FREE Strategy Call",
              subtitle: "Connect with our career transformation experts to discuss your goals and create a personalized roadmap",
              formType: "strategy_call"
            })}
            className="underline hover:text-blue-200 transition-colors cursor-pointer font-medium"
          >
            Book a FREE strategy call today! →
          </button>
        </span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="text-2xl font-bold text-white hover:text-blue-400 transition-colors cursor-pointer">
              1to10x
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors">
              Home
            </Link>
            <div className="relative group">
              <button className="flex items-center text-gray-300 hover:text-white transition-colors py-2">
                Courses <ChevronDown className="ml-1 h-4 w-4 transition-transform group-hover:rotate-180" />
              </button>
              {/* Compact dropdown menu - Together.ai style */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 w-[420px] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-white text-lg font-semibold mb-1">Popular Courses</h3>
                    <p className="text-gray-400 text-sm">1to10x certificate programs</p>
                  </div>
                  
                  <div className="space-y-1">
                    {/* Data Analyst Course */}
                    <Link to="/data-analyst-course" className="group/item flex items-center p-3 rounded-lg hover:bg-gray-800 transition-all duration-200">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-600/20 rounded-lg flex items-center justify-center mr-3 group-hover/item:bg-blue-600/30 transition-colors">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 3V21H21" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9 9L12 6L16 10L20 6" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="9" cy="9" r="2" fill="#60A5FA"/>
                          <circle cx="12" cy="6" r="2" fill="#60A5FA"/>
                          <circle cx="16" cy="10" r="2" fill="#60A5FA"/>
                          <circle cx="20" cy="6" r="2" fill="#60A5FA"/>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium text-sm mb-1 group-hover/item:text-blue-400 transition-colors truncate">
                          10x Data Analyst and AI Complete Certificate Program
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-semibold">FEATURED</span>
                          <span>6 months</span>
                        </div>
                      </div>
                    </Link>

                    {/* Agentic AI Course */}
                    <Link to="/agentic-ai-course" className="group/item flex items-center p-3 rounded-lg hover:bg-gray-800 transition-all duration-200">
                      <div className="flex-shrink-0 w-8 h-8 bg-purple-600/20 rounded-lg flex items-center justify-center mr-3 group-hover/item:bg-purple-600/30 transition-colors">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#A855F7"/>
                          <path d="M2 17L12 22L22 17" stroke="#A855F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M2 12L12 17L22 12" stroke="#A855F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="7" r="1.5" fill="#1F2937"/>
                          <circle cx="7" cy="9.5" r="1" fill="#1F2937"/>
                          <circle cx="17" cy="9.5" r="1" fill="#1F2937"/>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium text-sm mb-1 group-hover/item:text-purple-400 transition-colors truncate">
                          10x Agentic AI and Automation Mastery
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-semibold">MOST POPULAR</span>
                          <span>4 months</span>
                        </div>
                      </div>
                    </Link>

                    {/* Generative AI Course */}
                    <Link to="/generative-ai-course" className="group/item flex items-center p-3 rounded-lg hover:bg-gray-800 transition-all duration-200">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-600/20 rounded-lg flex items-center justify-center mr-3 group-hover/item:bg-green-600/30 transition-colors">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C13.1046 2 14 2.89543 14 4V6C14 7.10457 13.1046 8 12 8C10.8954 8 10 7.10457 10 6V4C10 2.89543 10.8954 2 12 2Z" fill="#10B981"/>
                          <path d="M12 16C13.1046 16 14 16.8954 14 18V20C14 21.1046 13.1046 22 12 22C10.8954 22 10 21.1046 10 20V18C10 16.8954 10.8954 16 12 16Z" fill="#10B981"/>
                          <path d="M4 10C2.89543 10 2 10.8954 2 12C2 13.1046 2.89543 14 4 14H6C7.10457 14 8 13.1046 8 12C8 10.8954 7.10457 10 6 10H4Z" fill="#10B981"/>
                          <path d="M18 10C16.8954 10 16 10.8954 16 12C16 13.1046 16.8954 14 18 14H20C21.1046 14 22 13.1046 22 12C22 10.8954 21.1046 10 20 10H18Z" fill="#10B981"/>
                          <circle cx="12" cy="12" r="3" stroke="#10B981" strokeWidth="2" fill="none"/>
                          <path d="M8 8L9.5 9.5M16 8L14.5 9.5M8 16L9.5 14.5M16 16L14.5 14.5" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium text-sm mb-1 group-hover/item:text-green-400 transition-colors truncate">
                          Become a Generative AI Expert
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="bg-green-500 text-white px-2 py-0.5 rounded text-xs font-semibold">TRENDING</span>
                          <span>3 months</span>
                        </div>
                      </div>
                    </Link>
                  </div>

                  {/* Featured programs CTA */}
                  <div className="border-t border-gray-700 mt-4 pt-4">
                    <a href="#featured-programs" 
                       className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-800 transition-all duration-200 group/cta">
                      <div>
                        <span className="text-white font-medium group-hover/cta:text-blue-400 transition-colors">Featured Programs</span>
                        <p className="text-gray-400 text-sm">Jump to course highlights</p>
                      </div>
                      <div className="text-blue-400 group-hover/cta:translate-x-1 transition-transform">→</div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <a href="https://academy.10xdataanalyst.com/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">
              Enrol Now
            </a>
            <Link to="/about" className="text-gray-300 hover:text-white transition-colors">
              About
            </Link>
            <Link to="/contact" className="text-gray-300 hover:text-white transition-colors">
              Contact Us
            </Link>
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <button 
              onClick={() => openPopup?.({
                title: "Book FREE Strategy Call",
                subtitle: "Connect with our career transformation experts to discuss your goals and create a personalized roadmap",
                formType: "strategy_call"
              })}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 lg:px-6 py-2 rounded-full transition-all duration-200 transform hover:scale-105 flex items-center gap-2 text-sm lg:text-base"
            >
              <span className="hidden lg:inline">Book FREE Strategy Call</span>
              <span className="lg:hidden">Book Call</span>
              <div className="w-5 h-5 lg:w-6 lg:h-6 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-xs">→</span>
              </div>
            </button>
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <div className="space-y-1">
              <div className="w-6 h-0.5 bg-gray-400"></div>
              <div className="w-6 h-0.5 bg-gray-400"></div>
              <div className="w-6 h-0.5 bg-gray-400"></div>
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-800">
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-gray-300" onClick={() => setIsMenuOpen(false)}>Home</Link>
              <Link to="/data-analyst-course" className="text-gray-300" onClick={() => setIsMenuOpen(false)}>10x Data Analyst & AI Program</Link>
              <Link to="/agentic-ai-course" className="text-gray-300" onClick={() => setIsMenuOpen(false)}>Agentic AI & Automation</Link>
              <Link to="/generative-ai-course" className="text-gray-300" onClick={() => setIsMenuOpen(false)}>Generative AI Expert</Link>
              <a href="https://academy.10xdataanalyst.com/" target="_blank" rel="noopener noreferrer" className="text-gray-300">All Courses</a>
              <a href="https://academy.10xdataanalyst.com/" target="_blank" rel="noopener noreferrer" className="text-gray-300">Enrol Now</a>
              <Link to="/about" className="text-gray-300" onClick={() => setIsMenuOpen(false)}>About</Link>
              <Link to="/contact" className="text-gray-300" onClick={() => setIsMenuOpen(false)}>Contact Us</Link>
              <button 
                onClick={() => {
                  openPopup?.({
                    title: "Book FREE Strategy Call",
                    subtitle: "Connect with our career transformation experts to discuss your goals and create a personalized roadmap",
                    formType: "strategy_call"
                  });
                  setIsMenuOpen(false);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full text-center font-medium"
              >
                Book FREE Strategy Call
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
